<template>
  <div class="screen-next-section-btn" :class="{ active: !disabled }" :aria-disabled="disabled">
    <div class="screen-next-section-btn-enter" v-if="!hideEnter">
      press <strong>Enter</strong>
      <svg class="screen-next-enter-svg" width="15" height="8" viewBox="0 0 15 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12.9599 0V3.88022H5.28385V2.13387V0.988533L4.33309 1.62002L0.772849 3.98476L0 4.49419L0.772849 5.00377L4.33309 7.36866L5.28385 8V6.85481V5.10846H13.5722H14.1843V4.49419V0H12.9599Z"
        />
      </svg>
    </div>
    <button
      type="button"
      :disabled="disabled"
      class="screen-next-btn"
      :class="{ secondary, busy }"
      @click="navigate"
      :id="`screen-continue-${screenId}`"
      :data-test="`checkout-save-and-continue-${screenId}`"
    >
      <span class="screen-next-btn-text">
        {{ text ? text : 'Save &amp; Continue' }}
      </span>
      <svg v-if="!hideIcon && !busy" width="16" height="16" viewBox="0 0 24 24" :fill="iconColor" class="screen-next-btn-icon">
        <path :d="mdiOpenInNew" />
      </svg>
      <span v-if="busy" class="screen-next-btn-spinner"></span>
    </button>
  </div>
</template>
<script setup lang="ts">
import { mdiOpenInNew } from '@mdi/js'
import { useAmplitude } from '@outputinc/o-analytics'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useScreenStore } from '@/store/ScreenStore'
import { usePlanStore } from '@/store/PlanStore'

const screenStore = useScreenStore()
const planStore = usePlanStore()

const { track, Events } = useAmplitude()
const { currentScreen, busy } = storeToRefs(screenStore)
const { setBusy } = screenStore
const { selectedPlanProduct } = storeToRefs(planStore)

const screenId = computed(() => currentScreen.value.id)

const emit = defineEmits(['navigateClick'])

const props = defineProps({
  completeStep: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  hideEnter: {
    type: Boolean,
    default: false
  },
  hideIcon: {
    type: Boolean,
    default: true
  },
  secondary: {
    type: Boolean,
    default: false
  },
  text: {
    type: String,
    default: null
  },
  onClick: {
    type: Function,
    default: () => {
      return new Promise(resolve => {
        resolve(true)
      })
    }
  }
})

const iconColor = '#222222'

const navigate = () => {
  setBusy(true)
  track(Events.CHECKOUT_SUBSCRIPTION_UI_SAVE_AND_CONTINUE, { id: screenId.value, products: [selectedPlanProduct.value] })
  props.onClick().then((e: any) => {
    if (!e) {
      setBusy(false)
      return false
    }

    emit('navigateClick')
  })
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/inc/variables';

.screen-next-enter-svg {
  margin-left: 2px;
  margin-top: -2px;

  path {
    fill: var(--color-text-primary);
  }
}

.screen-next-section-btn {
  margin-top: $spacer-4;
  font-weight: 500; // Required for safari to match chrome

  &-enter {
    color: var(--color-text-primary);
    font-size: 10px;
    line-height: 1.6;
    margin-bottom: $spacer-2;
    text-align: center;
    visibility: hidden;
  }

  @media screen and (min-width: $grid-breakpoint-md) {
    &.active {
      .screen-next-section-btn-enter {
        visibility: visible;
      }
    }
  }
}

.screen-next-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: $spacer-2;
  width: 100%;
  padding: 12px $spacer-4;
  font-family: $font-family-pilat-bold;
  font-size: $fs0;
  font-weight: $font-weight-bold;
  line-height: $btn-line-height;
  text-align: center;
  white-space: $btn-white-space;
  background-color: var(--color-accent-primary);
  border: $btn-border-width solid var(--color-accent-primary);
  border-radius: $border-radius-2xl;
  color: $gray5;
  cursor: pointer;
  transition: $transition-base;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: scale(1.03);
    transition: transform 0.2s ease-in-out;
  }

  &:disabled {
    border-color: var(--color-btn-disabled);
    background-color: var(--color-btn-disabled);
    color: var(--color-btn-disabled-text);
    cursor: not-allowed;
  }

  &.secondary {
    background-color: transparent;
    color: var(--color-text-primary);
  }

  &-text {
    display: inline-block;
  }

  &-icon {
    flex-shrink: 0;
  }

  &-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.75s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
